// src/components/company/AddFarmerModal.jsx
import React, { useState, useEffect, useCallback } from "react";
import { ref, onValue, set, off } from "firebase/database";
import { database } from "../../../firebaseConfig";
import toast from "react-hot-toast";
import { DEFAULT_COST_PER_KG } from "../../../constants/farmerConstants";

const AddFarmerModal = ({ open, onClose, companyName }) => {
  // Use the shared exported database instance
  const db = database;

  // Fields
  const [cloudName, setCloudName] = useState("");
  const [location, setLocation] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [idNumber, setIdNumber] = useState("");
  const [selectedHat, setSelectedHat] = useState("Gold");
  const [selectedPaymentSpan, setSelectedPaymentSpan] = useState("Weekly");
  const [clientId, setClientId] = useState("");
  const [advanceBalance, setAdvanceBalance] = useState("0");
  const [costPerKg, setCostPerKg] = useState(String(DEFAULT_COST_PER_KG)); // Default cost per kg for buying milk
  const [saving, setSaving] = useState(false);
  const [buttonCooldown, setButtonCooldown] = useState(false);

  // Fetch the next client ID from Firebase counter
  useEffect(() => {
    if (!db) return undefined;
    const counterRef = ref(db, "A_Counter/Node/Client");

    // attach listener
    onValue(counterRef, (snapshot) => {
      const id = snapshot.val();
      if (id) setClientId(id);
    });

    // cleanup - use off() to remove listener
    return () => {
      off(counterRef);
    };
  }, [db]);

  // Generate OtherName (like userName)
  const generateUserName = (cloudName, id) => {
    return `${cloudName.replace(/\s+/g, "")}${id}`.toLowerCase();
  };

  // Clear fields
  const clearFields = () => {
    setCloudName("");
    setLocation("");
    setPhoneNumber("");
    setIdNumber("");
    setSelectedHat("Gold");
    setSelectedPaymentSpan("Weekly");
    setAdvanceBalance("0");
    setCostPerKg(String(DEFAULT_COST_PER_KG));
  };

  const handleBackdropClick = useCallback((e) => {
    if (saving || buttonCooldown) {
      e.stopPropagation();
      return;
    }
    onClose();
  }, [saving, buttonCooldown, onClose]);

  const endCooldown = useCallback(() => {
    setTimeout(() => setButtonCooldown(false), 1200);
  }, []);

  if (!open) return null;

  const handleSave = async () => {
    if (saving || buttonCooldown) return;
    setSaving(true);
    setButtonCooldown(true);

    if (!cloudName.trim()) {
      toast.error("Cloud name is required");
      setSaving(false);
      endCooldown();
      return;
    }

    if (!companyName) {
      toast.error("Company name not loaded yet");
      setSaving(false);
      endCooldown();
      return;
    }

    const userName = generateUserName(cloudName, clientId);
    const now = new Date().toISOString();

    const clientData = {
      Id: parseInt(clientId),
      CloudName: cloudName,
      FirstName: "",
      MiddleName: "",
      LastName: "",
      OtherName: userName,
      Address: location,
      PhoneNumber: phoneNumber,
      IdentificationNumber: idNumber,
      Email: "",
      House: selectedPaymentSpan,
      Hat: selectedHat,
      Status: "Active",
      CompanyName: companyName,
      Balance: 0,
      CreatedAt: now,
      Mode: "KCB",
      LinkedFarmer: "", // 👈 new field for mapping farmer relationships
      AdvanceBalance: parseFloat(advanceBalance) || 0, // 👈 advance payment balance
      CostPerKg: parseFloat(costPerKg) || DEFAULT_COST_PER_KG, // 👈 cost per kg for buying milk from farmer
    };

    try {
      const updates = {};

      // Paths
      updates[`Companies/${companyName}/ValidatedFarmers/${userName}`] = clientData;
      updates[`Companies/${companyName}/AIMilkValidatedFarmers/${userName}`] = clientData;
      updates[`NewValidatedFarmers/${userName}`] = clientData;

      // Increment client counter
      const newCounter = (parseInt(clientId) || 0) + 1;
      updates[`A_Counter/Node/Client`] = newCounter.toString();

      // Save to Firebase
      await Promise.all(
        Object.entries(updates).map(([path, data]) => set(ref(db, path), data))
      );

      toast.success(`${cloudName} added successfully`);
      clearFields();
      onClose();
    } catch (err) {
      console.error("Error adding farmer:", err);
      toast.error("Error adding farmer");
    } finally {
      setSaving(false);
      endCooldown();
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-40 flex justify-center items-center z-50"
      onClick={handleBackdropClick}
      style={{ cursor: saving || buttonCooldown ? "not-allowed" : "default" }}
    >
      <div
        className="bg-white rounded-xl p-6 shadow-xl w-96 relative"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="text-xl font-semibold text-cyan-700 mb-4">
          Add New Farmer
        </h2>

        <div className="space-y-3">
          <input
            type="text"
            placeholder="Cloud Name"
            value={cloudName}
            onChange={(e) => setCloudName(e.target.value)}
            className="border w-full px-3 py-2 rounded focus:outline-none focus:ring-2 focus:ring-cyan-500"
          />
          <input
            type="text"
            placeholder="Location"
            value={location}
            onChange={(e) => setLocation(e.target.value)}
            className="border w-full px-3 py-2 rounded focus:outline-none focus:ring-2 focus:ring-cyan-500"
          />
          <input
            type="text"
            placeholder="Phone Number"
            value={phoneNumber}
            onChange={(e) => setPhoneNumber(e.target.value)}
            className="border w-full px-3 py-2 rounded focus:outline-none focus:ring-2 focus:ring-cyan-500"
          />
          <input
            type="text"
            placeholder="ID Number"
            value={idNumber}
            onChange={(e) => setIdNumber(e.target.value)}
            className="border w-full px-3 py-2 rounded focus:outline-none focus:ring-2 focus:ring-cyan-500"
          />

          <input
            type="number"
            placeholder={`Cost Per Kg (KES, default: ${DEFAULT_COST_PER_KG})`}
            value={costPerKg}
            onChange={(e) => setCostPerKg(e.target.value)}
            step="0.01"
            min="0"
            className="border w-full px-3 py-2 rounded focus:outline-none focus:ring-2 focus:ring-cyan-500"
          />

          <input
            type="number"
            placeholder="Advance Balance (KES)"
            value={advanceBalance}
            onChange={(e) => setAdvanceBalance(e.target.value)}
            step="0.01"
            min="0"
            className="border w-full px-3 py-2 rounded focus:outline-none focus:ring-2 focus:ring-cyan-500"
          />

          <div className="flex justify-between items-center space-x-2">
            <select
              value={selectedHat}
              onChange={(e) => setSelectedHat(e.target.value)}
              className="border px-3 py-2 rounded w-1/2 focus:outline-none focus:ring-2 focus:ring-cyan-500"
            >
              <option>Gold</option>
              <option>Silver</option>
              <option>Bronze</option>
            </select>

            <select
              value={selectedPaymentSpan}
              onChange={(e) => setSelectedPaymentSpan(e.target.value)}
              className="border px-3 py-2 rounded w-1/2 focus:outline-none focus:ring-2 focus:ring-cyan-500"
            >
              <option>Weekly</option>
              <option>2 - weeks</option>
              <option>Monthly</option>
            </select>
          </div>

          <div className="mt-4 flex justify-end gap-3">
            <button
              onClick={onClose}
              className={`px-4 py-2 rounded border border-gray-300 text-gray-600 hover:bg-gray-100 transition ${saving ? "opacity-60 cursor-not-allowed" : ""}`}
              disabled={saving}
            >
              Cancel
            </button>
            {buttonCooldown ? (
              <div className="px-4 py-2 rounded bg-gray-200 text-gray-500 text-sm">
                Finalizing...
              </div>
            ) : (
              <button
                onClick={handleSave}
                className={`px-4 py-2 rounded text-white transition ${saving ? "bg-cyan-300 cursor-not-allowed" : "bg-cyan-600 hover:bg-cyan-700"}`}
                disabled={saving}
              >
                {saving ? "Adding…" : "Add Farmer"}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddFarmerModal;
